import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { size, uniqBy, getOr } from 'lodash/fp';
import { Button, ButtonGroup, Content, Icon } from 'rsuite';
import { toastr } from 'react-redux-toastr';
import {
  createROPicklist,
  deleteROPicklist,
  fetchROPicklistForEdit,
  updateROPicklist
} from '../../../../redux/Reservation/sagas/routines';
import { resetROPicklistEdit } from '../../../../redux/Reservation/actions/routes/ro-picklist/edit';
import {
  getROPicklistEditData,
  getIsSaving,
  getIsSaved
} from '../../../../redux/Reservation/selectors/routes/ro-picklist/edit';
import { getIsDeleting } from '../../../../redux/Reservation/selectors/ro-picklist';
import { getIsSaving as getIsCloning } from '../../../../redux/Reservation/selectors/routes/ro-picklist/create';
import { fullAccess, modifyAccess } from '../../../../helpers/permission';
import * as PERMISSIONS from '../../../../constants/permissions/reservation';
import PageHeader from '../../../../components/PageHeader';
import PageLayout from '../../../../components/reservations/PageLayout';
import ROPicklistForm from '../../../../components/reservations/ROPicklistForm';
import AccessControl from '../../../../components/AccessControl';
import AccessDenied from '../../../../components/AccessDenied';
import ButtonToolbar from '../../../../components/reservations/ButtonToolbar';
import history from '../../../history';
import pagePathToUrl from '../../../../helpers/pagePathToUrl';
import {
  reservationsROPicklistListPath,
  reservationsROPicklistViewPath
} from '../../../Paths';
import Breadcrumb from '../../../../components/Breadcrumb';
import DeleteConfirmationModal from '../../../../components/DeleteConfirmationModal';
import clonePicklist from '../../../../helpers/clonePicklist';

export default () => {
  const { id } = useParams();
  const dispatch = useDispatch();
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const picklist = useSelector(getROPicklistEditData);
  const isSaving = useSelector(getIsSaving);
  const isSaved = useSelector(getIsSaved);
  const isCloning = useSelector(getIsCloning);
  const isDeleting = useSelector(getIsDeleting);

  useEffect(
    () => {
      dispatch(fetchROPicklistForEdit(id));
      return function reset() {
        dispatch(resetROPicklistEdit());
      };
    },
    [id]
  );

  const handleView = () => {
    history.push(pagePathToUrl(reservationsROPicklistViewPath, { id }));
  };

  const handleSubmit = body => {
    dispatch(updateROPicklist({ id, body }));
  };

  const handleClone = () => {
    const { items, categories } = picklist;
    const hasEmptyItems =
      size(uniqBy('categoryId', items)) !== size(categories);

    if (hasEmptyItems) {
      toastr.error(
        'Error',
        'Unable to Clone Pick List. Please fix form error first.'
      );
    } else {
      dispatch(createROPicklist({ body: clonePicklist(picklist) }));
    }
  };

  const handleShowDeleteModal = () => {
    setShowDeleteModal(true);
  };

  const handleHideDeleteModal = () => {
    setShowDeleteModal(false);
  };

  const handleConfirmDeleteItem = () => {
    setShowDeleteModal(false);
    dispatch(deleteROPicklist(picklist));
    setTimeout(() => {
      history.push(reservationsROPicklistListPath);
    }, 200);
  };

  return (
    <PageLayout>
      <AccessControl
        allowOnlyFor={modifyAccess(PERMISSIONS.RO_PICKLIST)}
        fallback={<AccessDenied />}
      >
        <Breadcrumb
          items={[
            {
              path: reservationsROPicklistListPath,
              label: 'Pick Lists'
            },
            {
              path: pagePathToUrl(reservationsROPicklistViewPath, { id }),
              label: getOr('...', 'name', picklist)
            },
            { label: `Edit Pick List` }
          ]}
        />
        <PageHeader title="Edit Pick List">
          <ButtonToolbar>
            <ButtonGroup>
              <Button size="sm" appearance="primary" onClick={handleView}>
                <Icon icon="eye" /> View
              </Button>
            </ButtonGroup>
            <ButtonGroup>
              <Button
                size="sm"
                appearance="primary"
                onClick={handleClone}
                disabled={isCloning}
              >
                <Icon icon="clone" /> Clone
              </Button>
            </ButtonGroup>
            <AccessControl allowOnlyFor={fullAccess(PERMISSIONS.RO_PICKLIST)}>
              <ButtonGroup>
                <Button size="sm" onClick={handleShowDeleteModal} color="red">
                  <Icon icon="trash" /> Delete
                </Button>
              </ButtonGroup>
            </AccessControl>
          </ButtonToolbar>
        </PageHeader>
        <Content style={{ marginTop: 0 }}>
          {picklist && (
            <ROPicklistForm
              onSubmit={handleSubmit}
              isSaving={isSaving}
              isSaved={isSaved}
              data={picklist}
            />
          )}

          <DeleteConfirmationModal
            message="Are you sure you want to delete this Pick List?"
            show={showDeleteModal}
            onHide={handleHideDeleteModal}
            onConfirm={handleConfirmDeleteItem}
            isDeleting={isDeleting}
          />
        </Content>
      </AccessControl>
    </PageLayout>
  );
};
